import { ReactNode } from 'react';
import { Box, Button, ButtonGroup, Divider, Typography } from '@mui/material';
import { type Theme } from '@mui/material/styles';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { primarySoft } from '../../../../../../theme';
import { filledButtonSx } from '../shared/buttonStyles';
import { CashdeskBlock, TextAlign } from './cashdeskBlocks';
import ToolPanel from './ToolPanel';

const MIN_FONT_SIZE = 10;
const MAX_FONT_SIZE = 82;
const FONT_STEP = 4;
const iconSx = { fontSize: '1rem' };
const groupSx = { '& .MuiButton-root': { minWidth: 34, px: 0.5, py: 0.5, borderColor: 'divider' } };

const ALIGNMENTS: Array<{ value: TextAlign; icon: ReactNode }> = [
  { value: 'left', icon: <FormatAlignLeftIcon sx={iconSx} /> },
  { value: 'center', icon: <FormatAlignCenterIcon sx={iconSx} /> },
  { value: 'right', icon: <FormatAlignRightIcon sx={iconSx} /> },
  { value: 'justify', icon: <FormatAlignJustifyIcon sx={iconSx} /> },
];

interface CashdeskTextPanelProps {
  selected: CashdeskBlock | null;
  onAddText: () => void;
  onUpdate: (patch: Partial<CashdeskBlock>) => void;
}

function CashdeskTextPanel({ selected, onAddText, onUpdate }: CashdeskTextPanelProps) {
  const formatSx = (active: boolean) => ({
    color: active ? 'primary.main' : 'text.primary',
    backgroundColor: active ? (theme: Theme) => primarySoft(theme, 0.09) : 'transparent',
  });

  return (
    <ToolPanel icon={<TextFieldsIcon sx={iconSx} />} title="Zone de texte">
      <Button variant="contained" size="small" startIcon={<TextFieldsIcon fontSize="small" />} onClick={onAddText} sx={{ ...filledButtonSx(), mb: 2 }}>
        Insérer une zone de texte
      </Button>

      {selected?.type === 'text' && (
        <>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>
            Mise en forme — bloc sélectionné
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <ButtonGroup size="small" variant="outlined" sx={groupSx}>
              <Button onClick={() => onUpdate({ fontWeight: selected.fontWeight === 'bold' ? 'normal' : 'bold' })} sx={formatSx(selected.fontWeight === 'bold')} title="Gras">
                <FormatBoldIcon sx={iconSx} />
              </Button>
              <Button onClick={() => onUpdate({ textDecoration: selected.textDecoration === 'underline' ? 'none' : 'underline' })} sx={formatSx(selected.textDecoration === 'underline')} title="Souligné">
                <FormatUnderlinedIcon sx={iconSx} />
              </Button>
              <Button onClick={() => onUpdate({ fontStyle: selected.fontStyle === 'italic' ? 'normal' : 'italic' })} sx={formatSx(selected.fontStyle === 'italic')} title="Italique">
                <FormatItalicIcon sx={iconSx} />
              </Button>
            </ButtonGroup>

            <ButtonGroup size="small" variant="outlined" sx={groupSx}>
              {ALIGNMENTS.map(({ value, icon }) => (
                <Button key={value} onClick={() => onUpdate({ textAlign: value })} sx={formatSx(selected.textAlign === value)}>{icon}</Button>
              ))}
            </ButtonGroup>

            <ButtonGroup size="small" variant="outlined" sx={groupSx}>
              <Button onClick={() => selected.fontSize < MAX_FONT_SIZE && onUpdate({ fontSize: selected.fontSize + FONT_STEP })} title="Augmenter taille">
                <TextIncreaseIcon sx={iconSx} />
              </Button>
              <Button onClick={() => selected.fontSize > MIN_FONT_SIZE && onUpdate({ fontSize: selected.fontSize - FONT_STEP })} title="Réduire taille">
                <TextDecreaseIcon sx={iconSx} />
              </Button>
            </ButtonGroup>
          </Box>
        </>
      )}
    </ToolPanel>
  );
}

export default CashdeskTextPanel;
